import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Grid,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getBrand } from '../config/brand.js';
import InterviewFlowDemoPage from './demo/InterviewFlowDemoPage';

function DemoPage() {
  if (getBrand() === 'interviewflow') {
    return <InterviewFlowDemoPage />;
  }
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const demoFeatures = [
    {
      title: 'CRM Management',
      description: 'See how easy it is to manage contacts, properties, and leads',
      icon: '👥',
    },
    {
      title: 'Workflow Automation',
      description: 'Watch our visual flow builder in action',
      icon: '⚡',
    },
    {
      title: 'Form Designer',
      description: 'Create beautiful forms with our drag-and-drop interface',
      icon: '📝',
    },
    {
      title: 'Analytics Dashboard',
      description: 'Real-time insights and reporting capabilities',
      icon: '📊',
    },
  ];

  const handleBackToLanding = () => {
    navigate('/');
  };

  const handleStartTrial = () => {
    navigate('/signup');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToLanding}
            sx={{
              position: 'absolute',
              left: 16,
              top: 16,
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            Back
          </Button>
          <Typography
            variant="h2"
            fontWeight={700}
            sx={{
              color: 'white',
              fontSize: { xs: '2rem', md: '3rem' },
              mb: 2,
            }}
          >
            See It In Action
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'rgba(255,255,255,0.9)',
              maxWidth: 600,
              mx: 'auto',
            }}
          >
            Watch how our platform transforms business operations with intelligent automation
          </Typography>
        </Box>

        {/* Demo Video Placeholder */}
        <Paper
          elevation={24}
          sx={{
            p: { xs: 2, md: 4 },
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            mb: 6,
          }}
        >
          <Box
            sx={{
              aspectRatio: '16/9',
              background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
              }}
            >
              <Button
                variant="contained"
                size="large"
                startIcon={<PlayArrowIcon />}
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: 'linear-gradient(45deg, #fbbf24, #f59e0b)',
                  color: '#000',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #f59e0b, #fbbf24)',
                    transform: 'scale(1.1)',
                  },
                  transition: 'all 0.3s ease',
                }}
              />
              <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
                Demo Video Coming Soon
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Features Grid */}
        <Grid container spacing={4} sx={{ mb: 6 }}>
          {demoFeatures.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Paper
                elevation={8}
                sx={{
                  p: 3,
                  textAlign: 'center',
                  height: '100%',
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <Typography variant="h1" sx={{ mb: 2 }}>
                  {feature.icon}
                </Typography>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {feature.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* CTA Section */}
        <Paper
          elevation={24}
          sx={{
            p: { xs: 3, md: 6 },
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            textAlign: 'center',
          }}
        >
          <Typography variant="h3" fontWeight={700} gutterBottom>
            Ready to Get Started?
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
            Join thousands of businesses already using our platform
          </Typography>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="center"
            sx={{ mb: 4 }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={handleStartTrial}
              startIcon={<CheckCircleIcon />}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 700,
                background: 'linear-gradient(45deg, #fbbf24, #f59e0b)',
                color: '#000',
                '&:hover': {
                  background: 'linear-gradient(45deg, #f59e0b, #fbbf24)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Start Free Trial
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/contact')}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 700,
                borderColor: '#6366f1',
                color: '#6366f1',
                '&:hover': {
                  borderColor: '#4f46e5',
                  backgroundColor: 'rgba(99, 102, 241, 0.1)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Schedule Demo
            </Button>
          </Stack>

          <Typography variant="body2" color="text.secondary">
            No credit card required • 14-day free trial • Cancel anytime
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
}

export default DemoPage;
